import { useEffect, useId, useRef, type ReactNode } from 'react'

import { Button } from './Button'

/**
 * A confirmation that names the consequence.
 *
 * §5.7 asks for "a confirmation dialog naming the consequence and the retention
 * window in words", and `consequence` is a required prop rather than an
 * optional one so a destructive action cannot ship without saying what it does.
 *
 * A native `<dialog>` with `showModal()`, for the same reason as the navigation
 * drawer: the focus trap, Escape, the backdrop and the inert background are the
 * platform's rather than re-implemented and subtly wrong.
 */
export function ConfirmDialog({
  open,
  title,
  consequence,
  detail,
  confirmLabel,
  destructive = false,
  busy = false,
  children,
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  /** What actually happens. Required — see above. */
  consequence: ReactNode
  /** Retention windows, reversibility, who is affected. */
  detail?: ReactNode
  confirmLabel: string
  destructive?: boolean
  busy?: boolean
  /** Anything the confirmation needs to collect — a reason, an acknowledgement. */
  children?: ReactNode
  onConfirm: () => void
  onCancel: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  /**
   * Derived, not fixed. The id was the literal "confirm-title", which is correct
   * for the one-dialog-per-route this app has today and silently wrong the day
   * a screen mounts two: both `aria-labelledby`s would resolve to the first
   * one's heading, and the second dialog would announce the wrong question.
   */
  const titleId = useId()
  /**
   * The consequence, announced.
   *
   * `showModal()` announces the dialog's name and its DESCRIPTION. With no
   * description a screen reader says "Delete your account?, dialog" and then
   * "Cancel, button" — where focus lands — and stops. The sentence the person
   * most needs is in the DOM and silent until they browse backwards for it.
   * `consequence` is a required prop so a destructive action cannot ship
   * without saying what it does; that guarantee held in the markup and not in
   * the accessibility tree.
   */
  const consequenceId = useId()

  /**
   * What had focus before this opened, so it can be given back.
   *
   * The platform is supposed to do this for a modal `<dialog>` and Chromium
   * does. **WebKit does not** — measured by `e2e/keyboard.spec.ts` running in
   * the `mobile-layout` project, where Escape closed the dialog and left focus
   * on `<body>`. `AppShell`'s drawer and `RowActions` both restore explicitly
   * already; this was the one that trusted the browser, and it was the one
   * nobody had ever tested with a keyboard.
   */
  const returnTo = useRef<HTMLElement | null>(null)

  /**
   * Idempotent by nulling, which is what lets three callers share it without
   * anyone reasoning about the order they fire in.
   */
  function restoreFocus() {
    returnTo.current?.focus()
    returnTo.current = null
  }

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) {
      returnTo.current = document.activeElement as HTMLElement | null
      dialog.showModal()
    } else if (!open && dialog.open) {
      dialog.close()
      restoreFocus()
    }
    /**
     * THE CASE THE FIRST VERSION MISSED, and it was the case both of this
     * dialog's new callers use.
     *
     * A dialog rendered as `{isConfirming && <ConfirmDialog open … />}` is
     * **unmounted, not closed**. The branch above never runs — the component is
     * gone before it can — and removing an open `<dialog>` from the document
     * fires no `close` event, so `onClose` never runs either. Focus was left on
     * `<body>`, which for a keyboard user means being dumped at the top of the
     * document with the skip link to tab back from.
     *
     * The `dialog.open` guard is what keeps this from firing twice on an
     * ordinary close: by then the branch above has already closed it and nulled
     * the ref.
     */
    return () => { if (dialog.open) restoreFocus() }
  }, [open])

  return (
    <dialog
      ref={ref}
      className="confirm"
      aria-labelledby={titleId}
      aria-describedby={consequenceId}
      // Escape and the backdrop close a <dialog> without telling React. This
      // also fires on a programmatic `close()`, so `restoreFocus` has usually
      // already run by then and does nothing -- which is why it nulls.
      onClose={() => { restoreFocus(); onCancel() }}
      onCancel={onCancel}
    >
      <h2 className="confirm__title" id={titleId}>{title}</h2>
      <p className="confirm__consequence" id={consequenceId}>{consequence}</p>
      {detail && <p className="confirm__detail">{detail}</p>}
      {children}
      <div className="confirm__actions">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button variant={destructive ? 'danger' : 'primary'} busy={busy} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </dialog>
  )
}
